import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { OMCS, OMC_LABELS } from '@shared/domain';
import { checkTaxIds } from '@shared/taxId';
import { actionKey, api } from '../../api';
import { LifecycleActions } from '../shared/LifecycleActions';
import { Badge, ConfirmDialog, Dialog, ErrorBox, Field, Loading, useBusy } from '../../ui';

interface OrgRow {
  id: string;
  name: string;
  status: string;
  gstin?: { value: string; status: string };
  pan?: { value: string; status: string };
  paymentDetails: null | { verification: { status: string }; hasBank: boolean; upiId?: string; ifsc?: string; accountLast4?: string; version: number };
  createdAt: string;
}

export function Platform() {
  const qc = useQueryClient();
  const orgs = useQuery({ queryKey: ['platform-orgs'], queryFn: () => api.get<OrgRow[]>('/api/platform/orgs') });
  const health = useQuery({ queryKey: ['platform-health'], queryFn: () => api.get<{ outbox: { _id: string; n: number }[]; sms: { _id: { purpose: string; status: string }; n: number }[]; dead: { type: string; lastError?: string }[]; smsProvider: string }>('/api/platform/health') });
  const [verify, setVerify] = useState<OrgRow | null>(null);
  const [showArchived, setShowArchived] = useState(false);
  const [method, setMethod] = useState('');
  const [onboard, setOnboard] = useState(false);
  const [f, setF] = useState({ name: '', line1: '', city: '', state: '', pincode: '', contact: '', adminName: '', adminPhone: '', location: '', code: '', gstin: '', pan: '', omc: '' });
  const [idResult, setIdResult] = useState<'VERIFIED' | 'REJECTED'>('VERIFIED');
  const [idCheck, setIdCheck] = useState<{ org: OrgRow; field: 'gstin' | 'pan' } | null>(null);
  const { busy, error, run } = useBusy();
  return (
    <div className="stack">
      <div className="card">
        <div className="row between"><h1>SuperIndent support</h1><button className="btn primary" onClick={() => setOnboard(true)}>+ Onboard a pump</button></div>
        <p className="muted">Setup and support only. Support cannot change money records or sign in as another user.</p>
      </div>
      <div className="card">
        <h2>Delivery health</h2>
        <p>SMS provider: <strong>{health.data?.smsProvider}</strong></p>
        <p>Jobs: {health.data?.outbox.map((o) => `${o._id.toLowerCase()} ${o.n}`).join(' · ')}</p>
        <p>SMS (7 days): {health.data?.sms.map((s) => `${s._id.purpose} ${s._id.status.toLowerCase()} ${s.n}`).join(' · ') || 'none'}</p>
        {health.data?.dead.length ? <div className="alert warn">Failed jobs: {health.data.dead.map((d) => `${d.type}: ${d.lastError}`).join('; ')}</div> : null}
      </div>
      <div className="card">
        <h2>Businesses</h2>
        <label className="check"><input type="checkbox" checked={showArchived} onChange={e => setShowArchived(e.target.checked)} /> Show archived businesses</label>
        {orgs.isLoading ? <Loading /> : <ErrorBox error={orgs.error} />}
        <div className="table-wrap">
          <table>
            <thead><tr><th>Business</th><th>GSTIN / PAN</th><th>Bank / UPI</th><th>Actions</th></tr></thead>
            <tbody>
              {orgs.data?.filter(o => showArchived || o.status !== 'ARCHIVED').map((o) => (
                <tr key={o.id}>
                  <td>{o.name}{o.status === 'ARCHIVED' ? <Badge>Archived</Badge> : null}</td>
                  <td>
                    {(['gstin', 'pan'] as const).map((k) => o[k] ? (
                      <div key={k}>{k.toUpperCase()} {o[k]!.value} <a href={k === 'gstin' ? 'https://services.gst.gov.in/services/searchtp' : 'https://eportal.incometax.gov.in/iec/foservices/#/pre-login/verifyYourPAN'} target="_blank" rel="noreferrer">Check on portal ↗</a> <Badge tone={o[k]!.status === 'VERIFIED' ? 'ok' : o[k]!.status === 'REJECTED' ? 'bad' : 'warn'}>{o[k]!.status.toLowerCase()}</Badge>{o[k]!.status !== 'VERIFIED' ? <button className="btn small" onClick={() => { setMethod(''); setIdResult('VERIFIED'); setIdCheck({ org: o, field: k }); }}>Mark checked</button> : null}</div>
                    ) : null)}
                    {!o.gstin && !o.pan ? '—' : null}
                  </td>
                  <td>{o.paymentDetails ? <>{o.paymentDetails.ifsc} ••{o.paymentDetails.accountLast4} {o.paymentDetails.upiId} <Badge tone={o.paymentDetails.verification.status === 'VERIFIED' ? 'ok' : 'warn'}>{o.paymentDetails.verification.status.toLowerCase()}</Badge>{o.paymentDetails.verification.status !== 'VERIFIED' ? <button className="btn small" onClick={() => setVerify(o)}>Mark checked</button> : null}</> : '—'}</td>
                  <td><LifecycleActions path={`/api/platform/orgs/${o.id}/lifecycle`} business /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      {idCheck ? (
        <ConfirmDialog open title={`Mark ${idCheck.field.toUpperCase()} as checked?`} lines={[['Business', idCheck.org.name], [idCheck.field.toUpperCase(), idCheck.org[idCheck.field]?.value ?? '']]} confirmLabel={idResult === 'VERIFIED' ? 'Mark checked' : 'Mark as not matching'} busy={busy} error={error} onClose={() => setIdCheck(null)} onConfirm={() => void run(async () => {
          await api.post(`/api/platform/orgs/${idCheck.org.id}/verify-identifier`, { field: idCheck.field, method, result: idResult });
          setIdCheck(null);
          await qc.invalidateQueries({ queryKey: ['platform-orgs'] });
        })}>
          <p className="muted">Look the number up on the government portal and compare the legal name:{' '}
            {idCheck.field === 'gstin' ? <a href="https://services.gst.gov.in/services/searchtp" target="_blank" rel="noreferrer">GST portal, Search Taxpayer</a> : <a href="https://eportal.incometax.gov.in/iec/foservices/#/pre-login/verifyYourPAN" target="_blank" rel="noreferrer">Income Tax portal, Verify PAN</a>}.
          </p>
          <Field label="Result">
            <select value={idResult} onChange={(e) => setIdResult(e.target.value as 'VERIFIED' | 'REJECTED')}>
              <option value="VERIFIED">Matches the portal</option>
              <option value="REJECTED">Does not match / not found</option>
            </select>
          </Field>
          {idResult === 'REJECTED' ? <p className="muted">The business keeps working. Its owners are notified to send the correct number. Use the Actions column if it should be suspended.</p> : null}
          <Field label={idResult === 'VERIFIED' ? 'How was it checked?' : 'What did not match?'} hint="e.g. GST portal search, legal name matches"><input value={method} onChange={(e) => setMethod(e.target.value)} /></Field>
        </ConfirmDialog>
      ) : null}
      {verify ? (
        <ConfirmDialog open title="Mark bank details as checked?" lines={[['Business', verify.name], ['IFSC', verify.paymentDetails?.ifsc ?? ''], ['Account ends', verify.paymentDetails?.accountLast4 ?? '']]} confirmLabel="Mark checked" busy={busy} error={error} onClose={() => setVerify(null)} onConfirm={() => void run(async () => {
          await api.post(`/api/platform/orgs/${verify.id}/verify-payment-details`, { method, version: verify.paymentDetails!.version });
          setVerify(null);
          await qc.invalidateQueries({ queryKey: ['platform-orgs'] });
        })}>
          <Field label="How was it checked?" hint="e.g. cancelled cheque seen, bank letter, ₹1 test transfer"><input value={method} onChange={(e) => setMethod(e.target.value)} /></Field>
        </ConfirmDialog>
      ) : null}
      <Dialog open={onboard} title="Onboard a pump (supplier)" onClose={() => setOnboard(false)}>
        <div className="stack">
          <Field label="Business name"><input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} /></Field>
          <Field label="Address"><input value={f.line1} onChange={(e) => setF({ ...f, line1: e.target.value })} /></Field>
          <div className="grid three">
            <Field label="City"><input value={f.city} onChange={(e) => setF({ ...f, city: e.target.value })} /></Field>
            <Field label="State"><input value={f.state} onChange={(e) => setF({ ...f, state: e.target.value })} /></Field>
            <Field label="PIN code"><input value={f.pincode} onChange={(e) => setF({ ...f, pincode: e.target.value })} /></Field>
          </div>
          <Field label="Business phone"><input value={f.contact} onChange={(e) => setF({ ...f, contact: e.target.value })} /></Field>
          <Field label="Owner name"><input value={f.adminName} onChange={(e) => setF({ ...f, adminName: e.target.value })} /></Field>
          <Field label="Owner mobile"><input value={f.adminPhone} onChange={(e) => setF({ ...f, adminPhone: e.target.value })} /></Field>
          <div className="grid two">
            <Field label="GSTIN"><input value={f.gstin} maxLength={15} onChange={(e) => setF({ ...f, gstin: e.target.value })} /></Field>
            <Field label="PAN"><input value={f.pan} maxLength={10} onChange={(e) => setF({ ...f, pan: e.target.value })} /></Field>
          </div>
          <Field label="Oil company">
            <select value={f.omc} onChange={(e) => setF({ ...f, omc: e.target.value })}>
              <option value="" disabled>Choose…</option>
              {OMCS.map((o) => <option key={o} value={o}>{OMC_LABELS[o]}</option>)}
            </select>
          </Field>
          <Field label="Pump name"><input value={f.location} onChange={(e) => setF({ ...f, location: e.target.value })} /></Field>
          <Field label="Pump short code"><input value={f.code} onChange={(e) => setF({ ...f, code: e.target.value })} /></Field>
          <ErrorBox error={error} />
          <button className="btn primary" disabled={busy} onClick={() => void run(async () => {
            const key = actionKey('platform-onboard');
            const problem = checkTaxIds({ gstin: f.gstin, pan: f.pan, state: f.state }, { gstinRequired: true });
            if (problem) throw new Error(problem);
            if (!f.omc) throw new Error("Choose the pump's oil company.");
            await api.post('/api/platform/suppliers', { org: { name: f.name, legalType: 'OTHER', address: { line1: f.line1, city: f.city, state: f.state, pincode: f.pincode }, gstin: f.gstin.trim(), pan: f.pan.trim() }, contactPhone: f.contact, adminName: f.adminName, adminPhone: f.adminPhone, location: { name: f.location, code: f.code, address: f.line1, fuelTypes: ['PETROL', 'DIESEL'], omc: f.omc } }, { idempotencyKey: key.key });
            key.done();
            setOnboard(false);
            setF({ name: '', line1: '', city: '', state: '', pincode: '', contact: '', adminName: '', adminPhone: '', location: '', code: '', gstin: '', pan: '', omc: '' });
            await qc.invalidateQueries({ queryKey: ['platform-orgs'] });
          })}>Create</button>
        </div>
      </Dialog>
    </div>
  );
}
